"use client";

/**
 * Shared time-grid pieces for the schedule CalendarView (mobile and desktop branches).
 * Splits the duplicated hour-gutter + per-day column rendering into reusable subcomponents so
 * both branches stay in sync.
 */

import type React from "react";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import {
  buildHourLabels,
  positionDayEntries,
  type TimedEntry,
} from "../../../../_logic/utils/schedule-calendar-layout";
import { CalendarEventCard } from "./CalendarEventCard";
import { getFirstLabelColor } from "./label-utils";
import { ScheduleNowLine } from "./ScheduleNowLine";
import {
  getScheduleItemRegistrationState,
  type ViewerRegistrationKeys,
} from "../../../../_logic/utils/schedule-registration-match";
import { isScheduleItemSignupClosed } from "../../../../_logic/utils/activity-signup-status";

/** Width of the leading hour-label gutter in pixels. Labels are just "12" with AM/PM stacked under it. */
export const HOUR_LABEL_WIDTH_PX = 52;

/** Floor for a rendered event card height so a very short event still fits one line. */
const MIN_EVENT_HEIGHT_PX = 22;

/** Space between a card and the next one below it. */
const EVENT_GAP_PX = 2;

/** Events shorter than this render the compact one-line layout (title only). */
const COMPACT_EVENT_HEIGHT_PX = 56;

interface CalendarHourGutterProps {
  hours: number[];
  gridHeight: number;
  /** Pixels per hour. Mobile passes a pinch-scaled value; desktop passes the base constant. */
  hourHeightPx: number;
}

/** Renders the leading hour-label column shared by mobile + desktop time grids. */
export function CalendarHourGutter({ hours, gridHeight, hourHeightPx }: CalendarHourGutterProps): React.ReactElement {
  return (
    <div style={{ width: HOUR_LABEL_WIDTH_PX, height: gridHeight }} className="relative shrink-0">
      {buildHourLabels(hours).map(({ hour, label, period }, idx) => (
        <div
          key={hour}
          className="absolute right-2 flex flex-col items-end whitespace-nowrap font-medium text-gray-400"
          style={{ top: idx * hourHeightPx }}
        >
          {/* Anchor idx 0 to the grid's top edge (its line is suppressed); pull every other number up by
              half its line so it centers on its hour line, with AM/PM hanging just below. */}
          <span className={`text-xs leading-4 ${idx === 0 ? "" : "-mt-2"}`}>{label}</span>
          {period && <span className="text-xs leading-4">{period}</span>}
        </div>
      ))}
    </div>
  );
}

interface CalendarDayColumnProps {
  entries: TimedEntry[];
  hours: number[];
  startHour: number;
  gridHeight: number;
  /** Pixels per hour. Mobile passes a pinch-scaled value; desktop passes the base constant. */
  hourHeightPx: number;
  labelMap: Map<number, ScheduleLabelRow>;
  onDelete?: (item: ScheduleItemRow) => void;
  onItemClick?: (item: ScheduleItemRow) => void;
  timeZone: string | null;
  className?: string;
  /** Viewer's status-bucketed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
  /** The column's date (`YYYY-MM-DD`), used to draw the "now" line on today's column only. */
  date: string;
  /** When true, this column is today and mounts the live "now" line (one ticking line per grid). */
  showNowLine?: boolean;
  /** Whether to show each event card's time line. Defaults to true (current behavior). */
  showTimeLabels?: boolean;
}

/**
 * Renders a single day's timed-entry column: faint hourly gridlines + absolute-positioned event cards.
 * The container is positioned by the parent (mobile = single column; desktop = one per visible day).
 */
export function CalendarDayColumn({
  entries,
  hours,
  startHour,
  gridHeight,
  hourHeightPx,
  labelMap,
  onDelete,
  onItemClick,
  timeZone,
  className,
  viewerRegistrations,
  date,
  showNowLine = false,
  showTimeLabels = true,
}: CalendarDayColumnProps): React.ReactElement {
  return (
    <div className={`relative flex-1 ${className ?? ""}`} style={{ height: gridHeight }}>
      {hours.slice(1, -1).map((h, hIdx) => (
        <div
          key={h}
          className="absolute inset-x-0 border-t border-gray-100"
          style={{ top: (hIdx + 1) * hourHeightPx }}
        />
      ))}
      {showNowLine && (
        <ScheduleNowLine
          date={date}
          timeZone={timeZone}
          startHour={startHour}
          hourHeightPx={hourHeightPx}
          gridHeight={gridHeight}
        />
      )}
      {positionDayEntries(entries, {
        startHour,
        hourHeightPx,
        minHeightPx: MIN_EVENT_HEIGHT_PX,
        gapPx: EVENT_GAP_PX,
      }).map((entry) => {
        const { top, height } = entry;
        const widthPct = 100 / entry.totalLanes;
        const leftPct = entry.lane * widthPct;
        const registrationState = getScheduleItemRegistrationState(entry.item, viewerRegistrations);
        const isRegistered = registrationState === "registered";
        const isWaitlisted = registrationState === "waitlisted";
        const isSignupClosed = isScheduleItemSignupClosed(entry.item);
        const canSignUp =
          entry.item.activity_id != null && viewerRegistrations != null && registrationState == null && !isSignupClosed;
        return (
          <div
            key={entry.item.id}
            className="absolute px-0.5"
            style={{ top, height, width: `${widthPct}%`, left: `${leftPct}%` }}
          >
            <CalendarEventCard
              item={entry.item}
              color={getFirstLabelColor(entry.item, labelMap)}
              onDelete={onDelete}
              onItemClick={onItemClick}
              hideTime={!showTimeLabels}
              compact={height < COMPACT_EVENT_HEIGHT_PX}
              narrow={entry.totalLanes >= 2}
              isRegistered={isRegistered}
              isWaitlisted={isWaitlisted}
              canSignUp={canSignUp}
              isSignupClosed={isSignupClosed}
            />
          </div>
        );
      })}
    </div>
  );
}
